import { request } from './http'

export interface CreditsBalance {
  user_id?: string
  enabled: boolean
  balance: number
  reserved: number
  available: number
  [key: string]: unknown
}

export interface CreditsLedgerTransaction {
  type: string
  amount: number
  balance_after: number
  created_at: number
  [key: string]: unknown
}

export interface CreditsLedger {
  transactions: CreditsLedgerTransaction[]
  limit: number
  offset: number
  [key: string]: unknown
}

export interface RedeemCreditsRequest {
  code: string
}

export interface PaymentStatus {
  enabled: boolean
  provider: string
}

export type CreditEstimateServiceKind = 'image' | 'audio' | 'video'

export interface CreditEstimateRequest {
  service_kind: CreditEstimateServiceKind
  unit_type?: 'count' | 'second'
  quantity: number
}

export interface CreditEstimate {
  user_id?: string
  enabled: boolean
  service_kind: CreditEstimateServiceKind
  unit_type: 'count' | 'second'
  quantity: number
  estimated_credits: number
  available_credits: number
  insufficient_credits: boolean
  pricing_source: string
  max_video_credits?: number
}

export interface UsageDailyRow {
  date: string
  credits: number
}

export interface UsageByModel {
  model_alias: string
  tokens: number
  credits: number
  source: 'local' | 'cloud'
}

export interface UsageDaily {
  month: string
  month_spend_credits: number
  cumulative_credits: number
  daily: UsageDailyRow[]
  by_model: UsageByModel[]
}

export type UsageServiceKind = 'text' | 'screenplay' | 'storyboard' | 'image' | 'audio' | 'video'

export interface UsageByServiceRow {
  service_kind: UsageServiceKind
  unit_type: string
  credits: number
  quantity?: number
  input_tokens: number
  output_tokens: number
  cached_input_tokens: number
  has_missing_token_split: boolean
}

export interface UsageByService {
  month?: string
  rows: UsageByServiceRow[]
  total: {
    quantity: number
    input_tokens: number
    output_tokens: number
    cached_input_tokens: number
    has_missing_token_split: boolean
    credits: number
    count: number
  }
}

function withUserId(path: string, userId?: string): string {
  if (!userId) return path
  const [pathname, query = ''] = path.split('?', 2)
  const qs = new URLSearchParams(query)
  qs.set('user_id', userId)
  return `${pathname}?${qs.toString()}`
}

export const creditsApi = {
  balance: (userId?: string): Promise<CreditsBalance> => request<CreditsBalance>(withUserId('/v1/credits/balance', userId)),
  ledger: (limit = 25, offset = 0, userId?: string): Promise<CreditsLedger> => {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    return request<CreditsLedger>(withUserId(`/v1/credits/ledger?${qs.toString()}`, userId))
  },
  redeem: (body: RedeemCreditsRequest, userId?: string): Promise<CreditsBalance> =>
    request<CreditsBalance>(withUserId('/v1/credits/redeem', userId), { method: 'POST', body }),
  estimate: (body: CreditEstimateRequest, userId?: string): Promise<CreditEstimate> =>
    request<CreditEstimate>(withUserId('/v1/credits/estimate', userId), { method: 'POST', body }),
  paymentStatus: (): Promise<PaymentStatus> => request<PaymentStatus>('/v1/billing/payment/status'),
  usageDaily: (month?: string, userId?: string): Promise<UsageDaily> => {
    const qs = new URLSearchParams()
    if (month) qs.set('month', month)
    return request<UsageDaily>(withUserId(`/v1/usage/daily?${qs.toString()}`, userId))
  },
  usageByService: (month?: string, userId?: string): Promise<UsageByService> => {
    const qs = new URLSearchParams()
    if (month) qs.set('month', month)
    return request<UsageByService>(withUserId(`/v1/usage/by-service?${qs.toString()}`, userId))
  },
}
